import React from 'react'
import PropTypes from 'prop-types'
import { DataGrid } from '@mui/x-data-grid'
import { Box, Typography } from '@mui/material'
import LockIcon from '@mui/icons-material/Lock'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { setSelectedOrder } from '../../../hold-allocation-slice'

function OrdersTableV2({ orders, isFetching }) {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const selectedBucket = useSelector((s) => s.holdAllocation.selectedBucket)

  const handleRowClick = (params) => {
    dispatch(setSelectedOrder(params.row))
    const bucketParam = selectedBucket && selectedBucket !== 'ALL' ? `?bucket=${selectedBucket}` : ''
    navigate(`/hold-allocation/${params.row.orderId}${bucketParam}`)
  }

  const rows = orders.map((o) => ({
    id: o.orderId,
    ...o,
  }))

  const columns = [
    {
      field: 'orderId',
      headerName: 'Order',
      flex: 0.4,
      minWidth: 80,
      sortable: true,
      renderCell: (p) => {
        const isLocked = p.row.locked
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {isLocked ? (
              <LockIcon sx={{ fontSize: 14, color: '#cc3f0c' }} />
            ) : (
              <LockOpenIcon sx={{ fontSize: 14, color: '#2db47d' }} />
            )}
            <Typography
              sx={{
                color: '#145a4b',
                fontFamily: 'AtlasGroteskRegular, sans-serif',
                fontSize: '0.82rem',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              {p.value}
            </Typography>
          </Box>
        )
      },
    },
    {
      field: 'dmsOrderNumber',
      headerName: 'P/O',
      flex: 0.55,
      minWidth: 120,
      sortable: true,
      renderCell: (p) => (
        <Typography sx={{ fontFamily: 'AtlasGroteskRegular, sans-serif', fontSize: '0.82rem', color: '#333' }}>
          {p.value}
        </Typography>
      ),
    },
    {
      field: 'lines',
      headerName: 'Lines',
      flex: 0.35,
      minWidth: 80,
      sortable: true,
      renderHeader: () => (
        <Box sx={{ lineHeight: 1.2 }}>
          <Typography sx={{ fontSize: '0.75rem', fontFamily: 'AtlasGroteskRegular, sans-serif', fontWeight: 600, color: '#333' }}>
            Lines
          </Typography>
          <Typography sx={{ fontSize: '0.65rem', fontFamily: 'AtlasGroteskRegular, sans-serif', color: '#888' }}>
            (Open/Total)
          </Typography>
        </Box>
      ),
      valueGetter: (value, row) => `${row.openLines} / ${row.totalLines}`,
      renderCell: (p) => (
        <Typography sx={{ fontFamily: 'AtlasGroteskRegular, sans-serif', fontSize: '0.82rem', color: '#333' }}>
          {p.row.openLines} / <Box component='span' sx={{ color: '#999' }}>{p.row.totalLines}</Box>
        </Typography>
      ),
    },
    {
      field: 'holdStatusDisplay',
      headerName: 'Hold Status',
      flex: 0.55,
      minWidth: 110,
      sortable: true,
      renderCell: (p) => {
        const val = p.value || 'No Hold'
        const lines = val.split('\n')
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', py: 0.5 }}>
            {lines.map((line, i) => (
              <Typography
                key={i}
                sx={{
                  fontFamily: 'AtlasGroteskRegular, sans-serif',
                  fontSize: '0.75rem',
                  color: line === 'No Hold' ? '#999' : '#333',
                  lineHeight: 1.4,
                }}
              >
                {line}
              </Typography>
            ))}
          </Box>
        )
      },
    },
    {
      field: 'holdDisplay',
      headerName: 'Hold(s)',
      flex: 0.45,
      minWidth: 90,
      sortable: true,
      renderCell: (p) => {
        const val = p.value
        if (!val || val === 'No Hold') {
          return <Typography sx={{ fontSize: '0.75rem', color: '#999' }}>-</Typography>
        }
        const codes = val.split(',')
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {codes.map((code) => (
              <Typography
                key={code.trim()}
                sx={{
                  fontFamily: 'AtlasGroteskRegular, sans-serif',
                  fontSize: '0.72rem',
                  fontWeight: 500,
                  color: '#555',
                  lineHeight: 1.3,
                }}
              >
                {code.trim()}
              </Typography>
            ))}
          </Box>
        )
      },
    },
    {
      field: 'quantity',
      headerName: 'Quantity',
      flex: 0.45,
      minWidth: 100,
      sortable: true,
      renderHeader: () => (
        <Box sx={{ lineHeight: 1.2 }}>
          <Typography sx={{ fontSize: '0.75rem', fontFamily: 'AtlasGroteskRegular, sans-serif', fontWeight: 600, color: '#333' }}>
            Quantity
          </Typography>
          <Typography sx={{ fontSize: '0.65rem', fontFamily: 'AtlasGroteskRegular, sans-serif', color: '#888' }}>
            Open/Total
          </Typography>
        </Box>
      ),
      valueGetter: (value, row) => `${row.openQty} / ${row.totalQty}`,
      renderCell: (p) => (
        <Typography sx={{ fontFamily: 'AtlasGroteskRegular, sans-serif', fontSize: '0.82rem', color: '#333' }}>
          {p.row.openQty?.toLocaleString()} / <Box component='span' sx={{ color: '#999' }}>{p.row.totalQty?.toLocaleString()}</Box>
        </Typography>
      ),
    },
    {
      field: 'orderValue',
      headerName: 'Order',
      flex: 0.6,
      minWidth: 140,
      sortable: true,
      renderHeader: () => (
        <Box sx={{ lineHeight: 1.2 }}>
          <Typography sx={{ fontSize: '0.75rem', fontFamily: 'AtlasGroteskRegular, sans-serif', fontWeight: 600, color: '#333' }}>
            Order
          </Typography>
          <Typography sx={{ fontSize: '0.65rem', fontFamily: 'AtlasGroteskRegular, sans-serif', color: '#888' }}>
            (Open/Total)
          </Typography>
        </Box>
      ),
      valueGetter: (value, row) => row.openValue,
      renderCell: (p) => {
        const fmtOpen = p.row.openValue != null
          ? `$${Number(p.row.openValue).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
          : '-'
        const fmtTotal = p.row.totalValue != null
          ? `$${Number(p.row.totalValue).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
          : '-'
        return (
          <Typography sx={{ fontFamily: 'AtlasGroteskRegular, sans-serif', fontSize: '0.82rem', color: '#333' }}>
            {fmtOpen} / <Box component='span' sx={{ color: '#999' }}>{fmtTotal}</Box>
          </Typography>
        )
      },
    },
    {
      field: 'orderDate',
      headerName: 'Order Date',
      flex: 0.6,
      minWidth: 140,
      sortable: true,
      valueFormatter: (value) => {
        if (!value) return '-'
        const d = new Date(value)
        return d.toLocaleString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true,
        })
      },
    },
    {
      field: 'shipToName',
      headerName: 'Ship-to Name',
      flex: 0.65,
      minWidth: 140,
      sortable: true,
      renderCell: (p) => (
        <Typography
          sx={{
            fontFamily: 'AtlasGroteskRegular, sans-serif',
            fontSize: '0.82rem',
            color: '#333',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {p.value}
        </Typography>
      ),
    },
    {
      field: 'shipAccount',
      headerName: 'Ship-to A/C #',
      flex: 0.5,
      minWidth: 110,
      sortable: true,
      renderCell: (p) => (
        <Typography sx={{ fontFamily: 'AtlasGroteskRegular, sans-serif', fontSize: '0.82rem', color: '#333' }}>
          {p.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'paymentTerms',
      headerName: 'Payment Terms',
      flex: 0.4,
      minWidth: 90,
      sortable: true,
      renderCell: (p) => (
        <Typography sx={{ fontFamily: 'AtlasGroteskRegular, sans-serif', fontSize: '0.82rem', color: '#333' }}>
          {p.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 40,
      sortable: false,
      disableColumnMenu: true,
      renderCell: () => (
        <ArrowForwardIosIcon sx={{ fontSize: 14, color: '#2db47d', cursor: 'pointer' }} />
      ),
    },
  ]

  const gridSx = {
    backgroundColor: '#ffffff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    minHeight: 200,
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: '#fafafa',
      borderBottom: '1px solid #e0e0e0',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontFamily: 'AtlasGroteskRegular, sans-serif',
      fontSize: '0.75rem',
      fontWeight: 600,
      color: '#333',
    },
    '& .MuiDataGrid-row': {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#f8faf8',
      },
    },
    '& .MuiDataGrid-cell': {
      fontFamily: 'AtlasGroteskRegular, sans-serif',
      fontSize: '0.82rem',
      color: '#333',
      borderBottom: '1px solid #f0f0f0',
      display: 'flex',
      alignItems: 'center',
    },
    '& .MuiDataGrid-footerContainer': {
      borderTop: '1px solid #e0e0e0',
    },
  }

  return (
    <DataGrid
      sx={gridSx}
      rows={rows}
      columns={columns}
      loading={isFetching}
      pageSizeOptions={[25, 50]}
      initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
      onRowClick={handleRowClick}
      disableColumnFilter
      disableRowSelectionOnClick
      autoHeight
      getRowHeight={() => 52}
    />
  )
}

OrdersTableV2.propTypes = {
  orders: PropTypes.arrayOf(PropTypes.object).isRequired,
  isFetching: PropTypes.bool.isRequired,
}

export default OrdersTableV2
